import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import type { FeatureKey } from '../../../common/models/feature-usage.model';
import { FEATURE_META } from './usage-meta';

/**
 * Every feature as a small glyph, in the same order on every row: dark when
 * the vendor used it in the period, faint when not. A row reads at a glance;
 * the count says how many.
 */
export function FeatureStrip({ used, highlight }: { used: FeatureKey[]; highlight?: FeatureKey | null }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2" data-testid="feature-strip">
      <ul className="flex items-center gap-0.5" aria-label={t('ADMIN_USAGE.TABLE.FEATURES')}>
        {FEATURE_META.map((f) => {
          const on = used.includes(f.key);
          const Icon = f.icon;
          const name = t(`ADMIN_USAGE.FEATURES.${f.key}`);
          return (
            <li
              key={f.key}
              title={`${name}: ${on ? t('ADMIN_USAGE.USED') : t('ADMIN_USAGE.NOT_USED')}`}
              aria-label={`${name}: ${on ? t('ADMIN_USAGE.USED') : t('ADMIN_USAGE.NOT_USED')}`}
              data-used={on}
              className={cn('flex size-5 items-center justify-center rounded', on ? 'text-foreground' : 'text-muted-foreground', highlight === f.key && 'bg-muted')}
              // Faint, not hidden: the gaps in a row are what it says.
              style={on ? undefined : { opacity: 0.3 }}
            >
              <Icon className="size-3.5" />
            </li>
          );
        })}
      </ul>
      <span className="text-xs text-muted-foreground tabular-nums">
        {used.length}/{FEATURE_META.length}
      </span>
    </div>
  );
}
